import { flowPathId, pulseColor } from './geometry'
import type { GraphEdge } from './types'

interface Props {
  edges: GraphEdge[]
  visited: Set<string>
  /** SMIL cannot be disabled by CSS, so under reduce-motion we render nothing. */
  reduced: boolean
}

const DOT_DELAYS = [0, 1.2]

/**
 * Middle layer: tracer dots riding the edge motion paths via SMIL.
 *
 * Its own SVG at zIndex 1, *below* the card layer at zIndex 5 — that is what
 * makes a dot disappear behind a card it passes rather than crossing the text.
 * Kept separate from EdgeLayer so the two can hold different z-indices.
 *
 * A dot only appears once both endpoints are lit, and never on a conversational
 * (activeOnly) link.
 */
export function TracerLayer({ edges, visited, reduced }: Props) {
  const active = edges.filter(
    (e) => !e.internal && !e.activeOnly && visited.has(e.from) && visited.has(e.to),
  )

  if (reduced || active.length === 0) return null

  return (
    <svg
      className="tl-tracers pointer-events-none absolute inset-0 h-full w-full"
      viewBox="0 0 100 100"
      preserveAspectRatio="none"
      style={{ zIndex: 1 }}
      aria-hidden="true"
    >
      {active.map((e, i) =>
        DOT_DELAYS.map((delay, j) => (
          <circle
            key={`${e.from}-${e.to}-${j}`}
            r={0.45}
            fill={pulseColor(i)}
            className="tl-edge-dot"
            opacity={0.75}
            // inline `color` is what makes the drop-shadow's currentColor work
            style={{ color: pulseColor(i) }}
          >
            <animateMotion dur="2.8s" begin={`${delay}s`} repeatCount="indefinite">
              <mpath href={`#${flowPathId(e.from, e.to)}`} />
            </animateMotion>
          </circle>
        )),
      )}
    </svg>
  )
}
